import React, { createContext, useContext, useState, useCallback } from "react";

interface RegisteredTable {
  id: string;
  name: string;
  onToggleConfig: () => void;
  onScrollTo: () => void;
  isConfigOpen: boolean;
}

interface SlateTableRegistryValue {
  tables: RegisteredTable[];
  register: (table: RegisteredTable) => void;
  unregister: (id: string) => void;
  update: (id: string, patch: Partial<RegisteredTable>) => void;
  /** ID of the table whose config panel is currently open (only one at a time) */
  activeConfigId: string | null;
  setActiveConfigId: (id: string | null) => void;
}

const SlateTableRegistryContext = createContext<SlateTableRegistryValue | null>(
  null
);

export function SlateTableRegistryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [tables, setTables] = useState<RegisteredTable[]>([]);
  const [activeConfigId, setActiveConfigId] = useState<string | null>(null);

  const register = useCallback((table: RegisteredTable) => {
    setTables((prev) => {
      if (prev.some((t) => t.id === table.id)) return prev;
      return [...prev, table];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setTables((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const update = useCallback((id: string, patch: Partial<RegisteredTable>) => {
    setTables((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...patch } : t))
    );
  }, []);

  return (
    <SlateTableRegistryContext.Provider
      value={{
        tables,
        register,
        unregister,
        update,
        activeConfigId,
        setActiveConfigId,
      }}
    >
      {children}
    </SlateTableRegistryContext.Provider>
  );
}

export function useSlateTableRegistry() {
  return useContext(SlateTableRegistryContext);
}
